import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Chip, IconButton, Tooltip, Button, Divider } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';

interface AutoUpgradeRelease {
  name: string;
  version: string;
  downloadUrl: string;
  checksum: string;
  releaseDateTime: string;
  size: number;
}

const DIRECT_URL = process.env.NODE_ENV === 'development'
  ? '/otn-pub/otn_software/autoupgrade.json'
  : '/au-release.php';
const ORACLE_URL = 'https://download.oracle.com/otn-pub/otn_software/autoupgrade.json';
const PROXY_URL  = `https://corsproxy.io/?${encodeURIComponent(ORACLE_URL)}`;
const CACHE_KEY  = 'au_release_v1';
const CACHE_TTL  = 60 * 60 * 1000;

const FALLBACK: AutoUpgradeRelease = {
  name: 'autoupgrade.jar',
  version: '26.3',
  downloadUrl: 'https://download.oracle.com/otn-pub/otn_software/autoupgrade.jar',
  checksum: '74f04e796d3ec8380f56d9ac4dfa2656f4e3d4e7f6cec42d2d08a728b7de617f',
  releaseDateTime: '2026-04-06T10:00:00.000Z',
  size: 7016621,
};

async function fetchRelease(): Promise<AutoUpgradeRelease> {
  try {
    const r = await fetch(DIRECT_URL);
    if (!r.ok) throw new Error('not ok');
    return await r.json();
  } catch {
    const r = await fetch(PROXY_URL);
    if (!r.ok) throw new Error('proxy not ok');
    return await r.json();
  }
}

function useAutoUpgradeRelease() {
  const [data, setData] = useState<AutoUpgradeRelease>(FALLBACK);
  const [live, setLive] = useState(false);

  useEffect(() => {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        const { payload, ts } = JSON.parse(cached);
        if (Date.now() - ts < CACHE_TTL) { setData(payload); setLive(true); return; }
      }
    } catch {}
    fetchRelease()
      .then((d: AutoUpgradeRelease) => {
        setData(d);
        setLive(true);
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ payload: d, ts: Date.now() })); } catch {}
      })
      .catch(() => {});
  }, []);

  return { data, live };
}

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <Tooltip title={copied ? 'Copied!' : label} arrow>
      <IconButton size="small" onClick={handle} sx={{ p: 0.25, flexShrink: 0 }}>
        {copied
          ? <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} />
          : <ContentCopyIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
        }
      </IconButton>
    </Tooltip>
  );
}

export default function AutoUpgradeReleaseCard() {
  const { data, live } = useAutoUpgradeRelease();

  const releaseDate = new Date(data.releaseDateTime).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
  const sizeMB = (data.size / 1_048_576).toFixed(1) + ' MB';
  const wgetCmd = `wget ${data.downloadUrl}`;

  return (
    <Paper
      variant="outlined"
      sx={theme => ({
        p: 0,
        mb: 2,
        background: 'background.paper',
        borderRadius: 3,
        overflow: 'hidden',
        width: '100%',
        border: `2.5px solid ${theme.palette.mode === 'light' ? '#003366' : '#00bcd4'}`,
      })}
    >
      {/* Header — matches GeneratedConfig */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: 3, py: 2,
        borderBottom: '1px solid', borderColor: 'divider',
        background: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)',
      }}>
        <SystemUpdateAltIcon sx={{ color: theme => theme.palette.mode === 'light' ? '#003366' : '#00bcd4', fontSize: 24 }} />
        <Typography variant="h6" sx={{
          fontWeight: 600, fontSize: '1rem',
          color: theme => theme.palette.mode === 'light' ? '#003366' : '#00bcd4',
          flex: 1,
        }}>
          Latest AutoUpgrade
        </Typography>
        <Chip
          label={`v${data.version}`}
          size="small"
          sx={theme => ({
            fontWeight: 700, fontSize: '0.75rem', height: 22,
            background: theme.palette.mode === 'light' ? '#003366' : '#00bcd4',
            color: '#fff',
          })}
        />
        <Tooltip
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: theme => theme.palette.mode === 'dark' ? '#1e1e1e' : '#fff',
                color: theme => theme.palette.mode === 'dark' ? '#e0e0e0' : '#222',
                border: '1px solid',
                borderColor: live ? '#4caf5044' : '#9e9e9e44',
                borderRadius: 2,
                boxShadow: 4,
                px: 1.5,
                py: 1,
              },
            },
            arrow: {
              sx: { color: theme => theme.palette.mode === 'dark' ? '#1e1e1e' : '#fff' },
            },
          }}
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                width: 7, height: 7, borderRadius: '50%',
                background: live ? '#4caf50' : '#9e9e9e',
                flexShrink: 0,
              }} />
              <Box sx={{
                ...(live && {
                  '@keyframes liveBlink': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.3 },
                  },
                  animation: 'liveBlink 2s ease-in-out infinite',
                }),
              }}>
                <Typography sx={{ fontWeight: 700, fontSize: 12, color: live ? '#4caf50' : '#9e9e9e', lineHeight: 1.2 }}>
                  {live ? 'LIVE' : 'CACHED'}
                </Typography>
                <Typography sx={{ fontSize: 12, lineHeight: 1.3, mt: 0.2 }}>
                  {live ? 'Real-time data fetched from Oracle' : 'Showing cached data — live fetch unavailable'}
                </Typography>
              </Box>
            </Box>
          }
        >
          <Box sx={{
            width: 8, height: 8, borderRadius: '50%',
            background: live ? '#4caf50' : '#9e9e9e',
            boxShadow: live ? '0 0 6px #4caf5080' : 'none',
            cursor: 'default',
            ...(live && {
              '@keyframes livePulse': {
                '0%':   { transform: 'scale(1)',    boxShadow: '0 0 0 0 #4caf5066' },
                '70%':  { transform: 'scale(1.15)', boxShadow: '0 0 0 6px #4caf5000' },
                '100%': { transform: 'scale(1)',    boxShadow: '0 0 0 0 #4caf5000' },
              },
              animation: 'livePulse 2s ease-in-out infinite',
            }),
          }} />
        </Tooltip>
      </Box>

      {/* Content */}
      <Box sx={{ px: 3, py: 2.5 }}>
        {/* File info */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1.5 }}>
          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.9rem', fontWeight: 600 }}>
            autoupgrade.jar
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Released {releaseDate}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            · {sizeMB}
          </Typography>
        </Box>

        {/* Checksum row */}
        <Box sx={theme => ({
          display: 'flex', alignItems: 'center', gap: 0.5,
          background: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.04)',
          borderRadius: 1, px: 1.5, py: 0.75, mb: 0.75,
        })}>
          <Typography sx={{
            fontFamily: 'monospace', fontSize: '0.7rem', color: 'text.disabled',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
            userSelect: 'all',
          }}>
            SHA-256: {data.checksum}
          </Typography>
          <CopyButton text={data.checksum} label="Copy SHA-256" />
        </Box>

        {/* wget command */}
        <Box sx={theme => ({
          display: 'flex', alignItems: 'center', gap: 0.5,
          background: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.04)',
          borderRadius: 1, px: 1.5, py: 0.75, mb: 2.5,
        })}>
          <Typography sx={{
            fontFamily: 'monospace', fontSize: '0.7rem', color: 'text.secondary',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
            userSelect: 'all',
          }}>
            <Box component="span" sx={{ color: theme => theme.palette.mode === 'dark' ? '#00bcd4' : '#003366', fontWeight: 700 }}>wget</Box>
            {' '}{data.downloadUrl}
          </Typography>
          <CopyButton text={wgetCmd} label="Copy wget command" />
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        {/* Download button — matches GeneratedConfig button style */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <Button
            component="a"
            href={data.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={theme => ({
              borderRadius: 5,
              fontWeight: 700,
              px: 4, py: 1,
              fontSize: '0.85rem',
              background: theme.palette.mode === 'light' ? '#003366' : '#00bcd4',
              color: '#fff',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              boxShadow: 'none',
              '&:hover': {
                background: theme.palette.mode === 'light' ? '#004080' : '#00acc1',
                boxShadow: 'none',
              },
            })}
          >
            Download autoupgrade.jar
          </Button>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic', textAlign: 'center', fontSize: '0.68rem' }}>
            File served directly from Oracle servers — not stored or distributed by this site.
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}
